import { RequirePermission } from "@components/common/require-permission";
import { runtimeConfig } from "@src/runtime-config";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import {
  MemberEvents,
  ProjectEvents,
  WorkspaceEvents,
} from "@utils/track-event/track-event-list";
import { SubjectTypeProps } from "@custom-types/base-types";
import { ActionButtonSx } from "@styles/common-styles";
import { Button } from "@flat-ui";

interface LegacyAdminPanelLinkProps extends SubjectTypeProps {
  /** ID of the entity in the legacy admin panel */
  id: string;
}

/** External link to legacy admin-panel */
export function LegacyAdminPanelLink({
  subjectType,
  id,
}: LegacyAdminPanelLinkProps): React.JSX.Element {
  const { trackEvent } = useTrackEvent();
  let path: string;

  function onClick(): void {
    switch (subjectType) {
      case "COMPANY":
      case "COMPANY_MEMBER":
      case "DEVICE":
        trackEvent({ name: WorkspaceEvents.openCompanyInLegacyAdminPanel });
        path = `admin#nav_dashboard/edit/${id}`;
        break;

      case "PROJECT":
        trackEvent({ name: ProjectEvents.openProjectInLegacyAdminPanel });
        path = `admin#nav_projects/search/${id}`;
        break;

      case "USER":
        trackEvent({ name: MemberEvents.openMemberInLegacyAdminPanel });
        path = `admin#nav_actions/search/${id}`;
        break;

      default: {
        // eslint-disable-next-line @typescript-eslint/naming-convention
        const _exhaustiveCheck: never = subjectType;
        throw new Error(
          `Unsupported legacy admin panel link as type: ${_exhaustiveCheck}`
        );
      }
    }

    window.open(
      new URL(path, runtimeConfig.urls.apiUrl).toString(),
      "_blank",
      "noopener,noreferrer"
    );
  }

  const legacyAdminPanelButton = (
    <Button sx={{ ...ActionButtonSx }} onClick={onClick} variant="secondary">
      Open in Legacy Admin Panel
    </Button>
  );

  return (
    <RequirePermission permission="general.openInLegacyAdmin">
      {legacyAdminPanelButton}
    </RequirePermission>
  );
}
